import { ChevronLeft, ChevronRight } from 'lucide-react'
import { DayPicker, DayPickerProps } from 'react-day-picker'

import { cn } from '@/lib/utils'

export type CalendarProps = DayPickerProps

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'w-fit rdp-root bg-background group/calendar p-3',
        '[--cell-radius:theme(borderRadius.md)] [--cell-size:theme(spacing.9)]',
        '[[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent',
        className,
      )}
      classNames={{
        months: 'flex gap-6 flex-col md:flex-row relative rdp-months',
        month: 'flex flex-col w-full gap-5 rdp-month',
        month_caption:
          'flex items-center justify-center h-[var(--cell-size)] w-full px-[var(--cell-size)] rdp-month_caption',
        caption_label: 'select-none font-medium text-sm rdp-caption_label',
        nav: 'flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between rdp-nav',
        button_previous: cn(
          'inline-flex items-center justify-center whitespace-nowrap transition-all',
          'disabled:pointer-events-none disabled:opacity-50',
          'outline-none select-none',
          'hover:bg-muted hover:text-foreground',
          'size-[var(--cell-size)] aria-disabled:opacity-50 p-0',
          'rdp-button_previous',
        ),
        button_next: cn(
          'inline-flex items-center justify-center whitespace-nowrap transition-all',
          'disabled:pointer-events-none disabled:opacity-50',
          'outline-none select-none',
          'hover:bg-muted hover:text-foreground',
          'size-[var(--cell-size)] aria-disabled:opacity-50 p-0',
          'rdp-button_next',
        ),
        month_grid: 'rdp-month_grid',
        weekdays: 'rdp-weekdays',
        weekday:
          'text-muted-foreground rounded-[var(--cell-radius)] flex-1 font-normal text-[0.8rem] select-none rdp-weekday',
        week: 'w-full mt-2 rdp-week',
        day: cn(
          'relative w-full h-full p-0 text-center',
          'group/day aspect-square select-none',
          'rdp-day',
        ),
        day_button: cn(
          'inline-flex items-center justify-center whitespace-nowrap transition-all duration-200',
          'disabled:pointer-events-none disabled:opacity-50',
          'outline-none select-none',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'rounded-[var(--cell-radius)]',
          'relative z-10 flex aspect-square size-auto w-full',
          'min-w-[var(--cell-size)] h-[var(--cell-size)] p-0',
          'border-0 leading-none font-normal text-sm',
          'rdp-day rdp-day_button',
        ),
        range_start:
          'bg-primary text-primary-foreground hover:bg-primary/90 rounded-l-[var(--cell-radius)]',
        range_end:
          'bg-primary text-primary-foreground hover:bg-primary/90 rounded-r-[var(--cell-radius)]',
        range_middle:
          'aria-selected:bg-accent/80 aria-selected:text-foreground',
        outside:
          'text-muted-foreground/50 aria-selected:text-muted-foreground rdp-outside',
        today: 'font-semibold',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          const Icon = orientation === 'left' ? ChevronLeft : ChevronRight
          return <Icon className="size-4 rdp-chevron" />
        },
      }}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
